import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';

import { useAlert, useApi } from '@app/hooks';
import { TableBase } from '@app/components/common/table/table-base';
import Dialog from '@app/components/common/modals/dialog';
import { SelectField, SwitchField, TextField } from '@app/components/common';
import { CategorySku, SubcategorySku } from '@app/api/models/sku';

import { useColumns } from './columns';

type SubcategoryDraft = Partial<SubcategorySku>;

export const SkuSubcategoriesTable: FC = () => {
  const api = useApi();
  const { error } = useAlert();

  const [subcategories, setSubcategories] = useState<SubcategorySku[]>([]);
  const [categories, setCategories] = useState<CategorySku[]>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<SubcategoryDraft>();
  const [currentDialog, setCurrentDialog] = useState<'edit' | 'create'>();

  const fetchList = useCallback(() => {
    setLoading(true);
    Promise.all([api.sku.getSubcategories(), api.sku.getCategories()]).then(
      ([subcategoriesRes, categoriesRes]) => {
        setLoading(false);
        setSubcategories(subcategoriesRes.data || []);
        setCategories(categoriesRes.data || []);
      }
    );
  }, [api]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoriesById = useMemo(
    () => categories.reduce<Record<number, string>>((acc, item) => {
      acc[item.id] = item.name;
      return acc;
    }, {}),
    [categories]
  );

  const onEdit = (subcategory: SubcategorySku) => {
    setCurrent(subcategory);
    setCurrentDialog('edit');
  };

  const columns = useColumns({ onEdit, categoriesById });

  const onClose = () => {
    setCurrent(undefined);
    setCurrentDialog(undefined);
  };

  const onSubmit = (subcategory?: SubcategoryDraft) => async () => {
    if (!subcategory?.name) {
      error('Введите название');
      return;
    }
    if (!subcategory.category_id) {
      error('Выберите категорию');
      return;
    }
    setLoading(true);
    if (subcategory.id) {
      await api.sku.updateSubcategory({
        id: subcategory.id,
        name: subcategory.name,
        category_id: subcategory.category_id,
        is_active: !!subcategory.is_active,
      });
    } else {
      await api.sku.createSubcategory({
        name: subcategory.name,
        category_id: subcategory.category_id,
        is_active: subcategory.is_active ?? true,
      });
    }
    fetchList();
    onClose();
  };

  const selectedCategory = current?.category_id
    ? { id: current.category_id, name: categoriesById[current.category_id] || '' }
    : undefined;

  return (
    <>
      <TableBase<SubcategorySku>
        title="Подкатегории SKU"
        data={subcategories}
        columns={columns}
        loading={loading}
        onCreateClick={() => {
          setCurrent({ is_active: true });
          setCurrentDialog('create');
        }}
      />
      <Dialog
        open={!!currentDialog}
        onClose={onClose}
        title={currentDialog === 'edit' ? 'Редактирование подкатегории' : 'Создание подкатегории'}
      >
        <Flex gap={2} flexDirection="column">
          <TextField
            id="name"
            label="Название"
            required
            value={current?.name || ''}
            onChange={(value) =>
              setCurrent({
                ...current,
                name: value,
              })
            }
          />
          <SelectField
            id="category_id"
            label="Категория"
            required
            options={categories}
            value={selectedCategory}
            onChange={(option) =>
              setCurrent({
                ...current,
                category_id: option ? Number(option.id) : undefined,
              })
            }
          />
          <SwitchField
            label="Активна"
            value={current?.is_active}
            onChange={(value) =>
              setCurrent({
                ...current,
                is_active: value,
              })
            }
            id="is_active"
          />
          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="outline" colorScheme="gray" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="solid" colorScheme="brand" onClick={onSubmit(current)}>
              Сохранить
            </Button>
          </Flex>
        </Flex>
      </Dialog>
    </>
  );
};

export default SkuSubcategoriesTable;
